import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  output,
  Renderer2,
  viewChild,
} from '@angular/core';
import { DeviceService } from '../../services/device-detector/device.service';

@Component({
  selector: 'rating-stars',
  templateUrl: './rating-stars.component.html',
  styleUrls: ['./rating-stars.component.scss'],
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RatingStarsComponent implements AfterViewInit {
  private renderer = inject(Renderer2);
  private deviceService = inject(DeviceService);

  readonly rating = input<number>(undefined);
  readonly size = input<'default' | 'small'>('default');
  readonly isDisabled = input<boolean>(false);
  readonly isSmall = input<boolean>(false);

  readonly quickReview = output<number>();

  readonly stopElement = viewChild<ElementRef>('stopElement');
  readonly stopElementGray = viewChild<ElementRef>('stopElementGray');
  readonly pathElement = viewChild<ElementRef>('pathElement');
  readonly ratingBarElement = viewChild<ElementRef>('ratingBarElement');

  readonly fillId = Math.random().toFixed(5);

  private ratingBarElParams: { left: number; width: number } | null = null;
  private ratingOnHover = 0;

  constructor() {
    effect(() => {
      this.rating();
      this.setFill();
    });
  }

  ngAfterViewInit(): void {
    this.setFill();
    this.setRatingBarParams();
  }

  private setFill(): void {
    const rating = this.rating();
    if (rating === undefined) { return; }

    const stopEl = this.stopElement();
    const pathEl = this.pathElement();
    if (!stopEl || !pathEl) { return; }

    this.renderer.setAttribute(stopEl.nativeElement, 'offset', `${rating / 5}`);
    this.renderer.setAttribute(pathEl.nativeElement, 'fill', `url(#${this.fillId})`);
  }

  setRatingBarParams(): void {
    if (this.deviceService.isPlatformServer()) { return; }

    const ratingBarEl = this.ratingBarElement();
    if (!ratingBarEl) { return; }

    const rect = ratingBarEl.nativeElement.getBoundingClientRect();
    this.ratingBarElParams = {
      left: rect.left,
      width: rect.width
    };
  }

  onMouseMove(e: MouseEvent): void {
    if (this.isDisabled()) { return; }
    if (!this.ratingBarElParams) { return; }

    const cursorPositionX = e.clientX;
    const { left: ratingBarElLeftPosition, width: ratingBarElWidth } = this.ratingBarElParams;
    const starsWidthOnHover = cursorPositionX - ratingBarElLeftPosition;
    const starWidth = ratingBarElWidth / 5;

    this.ratingOnHover = Math.ceil(starsWidthOnHover / starWidth);

    const stopEl = this.stopElement();
    if (stopEl) {
      this.renderer.setAttribute(stopEl.nativeElement, 'offset', `${this.ratingOnHover / 5}`);
    }
  }

  onMouseLeave(): void {
    if (this.isDisabled()) { return; }

    const stopEl = this.stopElement();
    if (stopEl) {
      this.renderer.setAttribute(stopEl.nativeElement, 'offset', `${this.rating() / 5}`);
    }
  }

  onClick(): void {
    if (this.isDisabled()) { return; }

    this.quickReview.emit(this.ratingOnHover);
  }
}
